import { CHAT_BUILD_VERSION, CHAT_BUILD_VERSION_LS_KEY } from "@/lib/cache/chatBuildVersion";

const REFRESH_ATTEMPTS_KEY = "chat_build_refresh_attempts";

let hubHookInstalled = false;

function readMetaBuildVersion(): string {
  if (typeof document === "undefined") return CHAT_BUILD_VERSION;
  const meta = document.querySelector('meta[name="chat-build"]')?.getAttribute("content")?.trim();
  return meta || CHAT_BUILD_VERSION;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function purgeBrowserHttpCaches(): Promise<void> {
  if (!("caches" in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

/** Prefs UI / thème à conserver lors d’une purge hub. */
const PRESERVE_LS_PREFIXES = [
  "aurelia-chat-",
  "chat_build_version",
  "suite_user_id",
  "chat_web_push_",
];

async function purgeChatClientCaches(): Promise<void> {
  try {
    const keep = new Set(PRESERVE_LS_PREFIXES);
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if ([...keep].some((p) => k === p || k.startsWith(p))) continue;
      if (k.startsWith("chat_") || k.startsWith("aurelia-chat")) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) {
      localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }

  if ("indexedDB" in window && typeof indexedDB.databases === "function") {
    try {
      const dbs = await indexedDB.databases();
      for (const db of dbs) {
        const name = db.name || "";
        if (/chat/i.test(name) && name) {
          indexedDB.deleteDatabase(name);
        }
      }
    } catch {
      /* ignore */
    }
  }
}

async function onChatCachesMustRefresh(reason: string): Promise<void> {
  const targetBuild = readMetaBuildVersion();

  try {
    navigator.serviceWorker.controller?.postMessage({ type: "CHAT_PURGE_CACHES" });
  } catch {
    /* ignore */
  }

  await Promise.race([purgeBrowserHttpCaches(), sleep(4_000)]);
  await Promise.race([purgeChatClientCaches(), sleep(4_000)]);

  try {
    localStorage.setItem(CHAT_BUILD_VERSION_LS_KEY, targetBuild);
  } catch {
    /* ignore */
  }

  if (import.meta.env.DEV) {
    console.info("[Chat] Caches purgés:", reason, targetBuild);
  }
}

/** Première visite : enregistre la version sans bloquer le mount React. */
export function syncChatBuildVersionOnLaunch(): void {
  try {
    const meta = readMetaBuildVersion();
    const stored = localStorage.getItem(CHAT_BUILD_VERSION_LS_KEY);
    if (stored == null) {
      localStorage.setItem(CHAT_BUILD_VERSION_LS_KEY, meta);
      sessionStorage.removeItem(REFRESH_ATTEMPTS_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** Hub iframe : purge caches client avant le reload unique de suite-shared (pas de double reload). */
export function installChatCacheGuard(): void {
  if (hubHookInstalled || typeof window === "undefined") return;
  hubHookInstalled = true;

  window.addEventListener("message", (event) => {
    if (event.data?.type !== "CLEAR_CACHE") return;
    void onChatCachesMustRefresh("hub_clear_cache");
  });
}

/** Hard refresh manuel (debug / grosse MAJ). */
export async function hardRefreshChatApp(): Promise<void> {
  await onChatCachesMustRefresh("manual");
  window.location.reload();
}

declare global {
  interface Window {
    chatForceRefresh?: () => Promise<unknown>;
    chatClearClientCaches?: () => Promise<void>;
  }
}

if (typeof window !== "undefined") {
  window.chatClearClientCaches = () => onChatCachesMustRefresh("console");
}
